import React, { forwardRef, useEffect, useState, useImperativeHandle } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import axios from "../../../config/axiosConfig";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const cellSx = {
  "& .MuiTableCell-root": { py: 0.1, px: 0.6, fontSize: 10.5, lineHeight: 1.3 },
};

// Mismo patrón que EquiposAsignadosOT.jsx (TKR): sin Marca/Estado/Operación, Serie
// clicable abre CentroEquipoDetalleDialog, ícono en el estado vacío. A diferencia de
// TKR (2 tablas fijas Radio/Batería), acá el "Tipo" es dinámico por servicio, así que
// va en una sola tabla con columna Tipo en vez de 2 tablas separadas.
const EquiposVariosAsignadosOT = forwardRef(({ cod_ot, servicio, onData }, ref) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [equipoDialog, setEquipoDialog] = useState(null);

  const fetchData = () => {
    if (!cod_ot) return;
    setLoading(true);
    const peticiones = [axios.get(`/api/historial-varios?cod_ot=${cod_ot}`)];
    // POC usa chips (equipo_chip/historial_chip) además de equipo_varios.
    if (servicio === "POC") peticiones.push(axios.get(`/api/historial-chip?cod_ot=${cod_ot}`));

    Promise.all(peticiones)
      .then(([resVarios, resChip]) => {
        const varios = (resVarios.data || []).map((v) => ({ ...v, esChip: false }));
        const chips = (resChip?.data || []).map((c) => ({
          id_historial: `chip-${c.id_historial}`,
          serie: c.numero,
          tipo: "Chip",
          modelo: c.modelo,
          ciudad_actual: c.ciudad_actual,
          ciudad: c.ciudad,
          observacion: c.observacion,
          esChip: true,
        }));
        const todos = [...varios, ...chips];
        setItems(todos);
        onData?.(todos);
      })
      .catch((err) => console.error("Error al cargar historial", err))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchData, [cod_ot, servicio]);

  useImperativeHandle(ref, () => ({ refetch: fetchData }));

  if (loading) return <CircularProgress size={20} />;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Equipos asignados
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340, borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={cellSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Serie</TableCell>
              <TableCell sx={headCellSx}>Tipo</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
              <TableCell sx={headCellSx}>Ciudad</TableCell>
              <TableCell sx={headCellSx}>Observación</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!items.length ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box sx={{ textAlign: "center", py: 1.5, color: "text.secondary" }}>
                    <Inventory2OutlinedIcon sx={{ fontSize: 28, mb: 0.5, opacity: 0.5 }} />
                    <Typography variant="body2">No se adicionaron equipos.</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : items.map((it) => (
              <TableRow key={it.id_historial}>
                <TableCell>
                  <Box
                    component="span"
                    onClick={() => setEquipoDialog({ tipo: it.esChip ? "chip" : "varios", identificador: it.serie })}
                    sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                  >
                    {it.serie}
                  </Box>
                </TableCell>
                <TableCell>{it.tipo}</TableCell>
                <TableCell>{it.modelo}</TableCell>
                <TableCell>{it.ciudad_actual || it.ciudad}</TableCell>
                <TableCell>{it.observacion}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
});

export default EquiposVariosAsignadosOT;
